import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import { useAiCache, AI_TTL } from '../contexts/AiCacheContext';
import toast from 'react-hot-toast';
import MedicalDiscussion from '../components/MedicalDiscussion';
import ConsultingDoctors from '../components/ConsultingDoctors';
import PatientReplyForm from '../components/PatientReplyForm';
import ClinicianActionModal from '../components/ClinicianActionModal';
import CareJourneyTimeline from '../components/CareJourneyTimeline';
import {
  ArrowLeft, MessageCircle, Clock, AlertTriangle, Tag, User as UserIcon,
  FileText, Stethoscope, Lightbulb, HelpCircle, User, Activity,
  ChevronRight, CheckCircle, TrendingUp, Heart, MessageSquare, XCircle, RotateCcw,
  Video, Home as HomeIcon, Pill, Loader2, Zap, ShieldAlert
} from 'lucide-react';

// Defined at module scope — never changes, avoids recreation on every render
const CLINICIAN_ACTIONS = [
  {
    key: 'virtual_visit',
    label: 'Schedule Virtual Visit',
    icon: Video,
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    text: 'I am scheduling a virtual visit to discuss your symptoms. You will receive a video call link shortly.',
  },
  {
    key: 'home_visit',
    label: 'Order Home Visit',
    icon: HomeIcon,
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    text: 'I am arranging a home visit so a nurse can check on you. You will hear from us within 24 hours to confirm the time.',
  },
  {
    key: 'pharmacist',
    label: 'Pharmacist Consult',
    icon: Pill,
    color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    text: 'I have requested a pharmacist consultation to review your medications and address any concerns.',
  },
];

const SymptomDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';
  const { getOrFetch } = useAiCache();

  const [symptom, setSymptom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // 'virtual_visit' | 'home_visit' | 'pharmacist'
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);

  useEffect(() => {
    loadSymptom();
  }, [id]);

  const loadSymptom = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/symptoms/${id}`);
      const data = response.data.data || response.data.symptom || response.data;
      setSymptom(data);
      // Fetch AI analysis for doctors (non-blocking)
      if (isDoctor) {
        setAiAnalysisLoading(true);
        getOrFetch(`symptom-analysis:${id}`, () => api.get(`/ai/symptom-analysis/${id}`).then(r => ({ interpretation: r.data.analysis, redFlags: r.data.redFlags || [] })), AI_TTL.SYMPTOM_ANALYSIS)
          .then(result => setAiAnalysis(result))
          .catch(() => {})
          .finally(() => setAiAnalysisLoading(false));
      }
    } catch (error) {
      toast.error('Failed to load symptom');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/symptoms/${id}/status`, { status: newStatus });
      toast.success(`Symptom ${newStatus === 'CLOSED' ? 'closed' : 'reopened'}`);
      loadSymptom();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleActionScheduled = () => {
    loadSymptom();
  };

  const sevCfg = {
    urgent: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', label: 'Urgent', dot: 'bg-red-600', icon: AlertTriangle },
    high: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', label: 'High', dot: 'bg-orange-600', icon: TrendingUp },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', label: 'Medium', dot: 'bg-amber-500', icon: Activity },
    low: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', label: 'Low', dot: 'bg-emerald-500', icon: Heart },
  };

  const categoryLabels = {
    general:       'General',
    pain:          'Pain',
    respiratory:   'Respiratory',
    digestive:     'Digestive',
    neurological:  'Neurological',
    cardiac:       'Cardiac',
    skin:          'Skin',
    mental_health: 'Mental Health',
    other:         'Other',
  };

  const timeSince = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const hours = Math.floor(ms / 3600000);
    if (hours < 1) return `${Math.floor(ms / 60000)}m ago`;
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 rounded-xl animate-pulse bg-slate-100" />
        <div className="h-64 rounded-3xl animate-pulse bg-slate-100" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 rounded-3xl animate-pulse bg-slate-100" />
          <div className="h-64 rounded-3xl animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!symptom) return null;

  const sev = sevCfg[symptom.severity] || sevCfg.medium;
  const SevIcon = sev.icon;
  const isOpen = symptom.status === 'OPEN';
  const responseCount = symptom.responses?.length || 0;
  // Unique doctors who have posted responses — consultingDoctors does not exist on Symptom model
  const doctorCount = new Set((symptom.responses || []).map(r => r.doctorId?._id || r.doctorId).filter(Boolean)).size;

  // Build timeline events
  const timeline = [];
  timeline.push({
    type: 'created',
    label: 'Symptom reported',
    actor: isDoctor ? symptom.userId?.name : 'You',
    date: symptom.createdAt,
    icon: FileText,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
  });

  // Build "started reviewing" events from unique doctors in responses
  const reviewingDoctors = [];
  const seenDoctorIds = new Set();
  (symptom.responses || []).forEach(r => {
    const dId = r.doctorId?._id || r.doctorId;
    if (dId && !seenDoctorIds.has(String(dId))) {
      seenDoctorIds.add(String(dId));
      reviewingDoctors.push({ id: String(dId), name: r.doctorId?.name || 'Unknown', date: r.createdAt });
    }
  });
  const currentUserId = user?._id?.toString() || user?.id?.toString();
  reviewingDoctors.forEach(d => {
    const isMe = d.id && currentUserId && d.id.toString() === currentUserId;
    timeline.push({
      type: 'consulting',
      label: isMe ? 'You started reviewing' : `Dr. ${d.name} started reviewing`,
      actor: isMe ? 'You' : d.name,
      date: d.date,
      icon: UserIcon,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
    });
  });

  if (symptom.responses) {
    symptom.responses.forEach(r => {
      const typeMap = {
        diagnosis: { icon: Stethoscope, color: 'text-red-500', bgColor: 'bg-red-50' },
        suggestion: { icon: Lightbulb, color: 'text-amber-500', bgColor: 'bg-amber-50' },
        question: { icon: HelpCircle, color: 'text-violet-500', bgColor: 'bg-violet-50' },
      };
      const config = typeMap[r.type] || { icon: FileText, color: 'text-primary', bgColor: 'bg-primary/10' };
      const responseAuthorId = (r.doctorId?._id || r.doctorId)?.toString();
      const isMeResponse = responseAuthorId && currentUserId && responseAuthorId === currentUserId;
      timeline.push({
        type: 'response',
        label: isMeResponse
          ? `You posted a ${r.type || 'observation'}`
          : `Dr. ${r.doctorId?.name || 'Unknown'} posted a ${r.type || 'observation'}`,
        detail: r.text?.slice(0, 100),
        actor: isMeResponse ? 'You' : r.doctorId?.name,
        date: r.createdAt,
        ...config,
      });
      if (r.patientReplies) {
        r.patientReplies.forEach(pr => {
          timeline.push({
            type: 'reply',
            label: 'Patient replied',
            detail: pr.content?.slice(0, 80),
            actor: 'Patient',
            date: pr.createdAt,
            icon: MessageCircle,
            color: 'text-emerald-500',
            bgColor: 'bg-emerald-50',
          });
        });
      }
    });
  }

  if (!isOpen && symptom.resolvedAt) {
    const resolverId = symptom.resolvedBy?.id;
    const resolverName = symptom.resolvedBy?.name;
    const isMe = resolverId && currentUserId && resolverId === currentUserId;
    timeline.push({
      type: 'resolved',
      label: isMe ? 'You marked this as resolved' : `Dr. ${resolverName || 'Care team'} marked as resolved`,
      date: symptom.resolvedAt,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    });
  }

  timeline.sort((a, b) => new Date(a.date) - new Date(b.date));

  const sevColorMap = { 'bg-red-600': '#ba1a1a', 'bg-orange-600': '#F97316', 'bg-amber-500': '#d97706', 'bg-emerald-500': '#00696b' };
  const sevGradMap = { 'bg-red-600': 'from-error/[0.03]', 'bg-orange-600': 'from-orange-500/[0.04]', 'bg-amber-500': 'from-amber-500/[0.05]', 'bg-emerald-500': 'from-secondary/[0.03]' };

  return (
    <div className="space-y-8">

      {/* ─── Hero Header Card ─── */}
      <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 overflow-hidden border border-outline-variant/10 relative">
        {/* Decorative gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-b ${sevGradMap[sev.dot] || 'from-primary/[0.02]'} to-transparent pointer-events-none`} />
        {/* Severity accent bar */}
        <div className="h-1.5 w-full relative z-10" style={{ backgroundColor: sevColorMap[sev.dot] || '#00478d' }} />

        <div className="p-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left: Title + meta */}
            <div className="flex items-start gap-5 flex-1 min-w-0">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${sev.bg}`}>
                <SevIcon className={`w-7 h-7 ${sev.text}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-extrabold px-3 py-1.5 rounded-full border ${sev.bg} ${sev.text} ${sev.border}`}>
                    <span className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                      {sev.label.toUpperCase()}
                    </span>
                  </span>
                  <span className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${
                    isOpen ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {isOpen ? 'ACTIVE' : 'RESOLVED'}
                  </span>
                </div>
                <h1 className="font-headline text-3xl font-extrabold text-slate-900 tracking-tight break-words">{symptom.title}</h1>
                {isDoctor && symptom.userId && (
                  <button
                    onClick={() => navigate(`/patient/${symptom.userId._id || symptom.userId}`)}
                    className="text-xs mt-2 flex items-center gap-1.5 font-semibold tracking-wider text-primary hover:text-primary/80 hover:underline transition-colors"
                  >
                    <UserIcon className="w-3.5 h-3.5" />
                    Patient: {symptom.userId.name}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Right: Quick stats */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="bg-white rounded-[1.25rem] px-5 py-4 text-center shadow-premium border border-outline-variant/10">
                <p className="text-3xl font-extrabold text-primary tracking-tighter">{responseCount}</p>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mt-1">Responses</p>
              </div>
              <div className="bg-white rounded-[1.25rem] px-5 py-4 text-center shadow-premium border border-outline-variant/10">
                <p className="text-3xl font-extrabold text-secondary tracking-tighter">{doctorCount}</p>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mt-1">Doctors</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mt-7 space-y-2">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Patient Description</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20">
              <p className="text-sm font-bold text-slate-900">{symptom.title}</p>
              <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed font-medium">{symptom.description}</p>
            </div>
          </div>

          {/* Meta row */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {symptom.category && (
              <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
                <Tag className="w-3 h-3" />
                {categoryLabels[symptom.category] || symptom.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            )}
            <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
              <Clock className="w-3 h-3" />
              {new Date(symptom.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
              {timeSince(symptom.createdAt)}
            </span>
          </div>
        </div>

        {/* Action buttons footer */}
        <div className="px-8 pb-8 flex flex-wrap items-center justify-between gap-3 relative z-10">
          {isDoctor && !isOpen && (
            <button
              onClick={() => handleStatusChange('OPEN')}
              className="text-xs font-bold text-slate-500 hover:text-slate-600 transition-colors uppercase tracking-widest"
            >
              Reopen Symptom
            </button>
          )}
          {!isDoctor && <div />}
          {/* Patients only see the chat button if a doctor has already sent a message */}
          {(isDoctor || symptom.chat?.some(msg => msg.senderRole === 'DOCTOR')) && (
            <button
              onClick={() => navigate(`/chat/${id}`)}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-primary transition-all shadow-sm hover:shadow-primary/25 active:scale-[0.98]"
            >
              <MessageSquare className="w-4 h-4" />
              Open Chat
            </button>
          )}
        </div>
      </div>

      {/* Care Journey — downstream care events for this symptom */}
      <CareJourneyTimeline symptomId={symptom?._id?.toString()} />

      {/* ─── AI Clinical Interpretation (doctor only) ─── */}
      {isDoctor && (aiAnalysisLoading || aiAnalysis) && (
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 space-y-3">
          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> AI Clinical Interpretation
          </p>
          {aiAnalysisLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
              <span className="text-sm text-blue-400">Analyzing symptom in clinical context…</span>
            </div>
          ) : (
            <>
              {aiAnalysis?.interpretation && (
                <p className="text-sm text-slate-700 leading-relaxed">{aiAnalysis.interpretation}</p>
              )}
              {aiAnalysis?.redFlags?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {aiAnalysis.redFlags.map((flag, i) => (
                    <span key={i} className="flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 rounded-full text-xs font-bold text-red-700">
                      <ShieldAlert className="w-3 h-3" /> {flag}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── Clinician Action Bar (doctor only) ─── */}
      {isDoctor && isOpen && (
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5">
          <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em] mb-3">Quick Clinical Actions</p>
          <div className="flex flex-wrap gap-3">
            {CLINICIAN_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  onClick={() => setActiveModal(action.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all hover:shadow-sm active:scale-[0.98] ${action.color}`}
                >
                  <Icon className="w-4 h-4" />
                  {action.label}
                </button>
              );
            })}
            {isOpen && (
              <button
                onClick={() => handleStatusChange('CLOSED')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-500 hover:bg-slate-50 transition-all"
              >
                <CheckCircle className="w-4 h-4" />
                Mark as Resolved
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Clinician Action Modal ─── */}
      {activeModal && symptom && (
        <ClinicianActionModal
          actionKey={activeModal}
          patientId={symptom.userId?._id || symptom.userId}
          symptomId={id}
          onClose={() => setActiveModal(null)}
          onScheduled={handleActionScheduled}
        />
      )}

      {/* ─── Two Column Layout ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content: Discussion */}
        <div className="lg:col-span-2">
          {isDoctor ? (
            <MedicalDiscussion
              symptom={symptom}
              onAddInsight={async (insight) => {
                await api.post(`/responses/${id}`, insight);
                loadSymptom();
              }}
              symptomId={id}
              consultedDoctorIds={symptom?.consultedDoctorIds || []}
              onConsultSuccess={loadSymptom}
            />
          ) : (
            <ConsultingDoctors symptom={symptom} />
          )}
        </div>

        {/* Sidebar: Activity Timeline */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-premium hover:shadow-premium-hover transition-all duration-500 border border-outline-variant/10 p-7 relative overflow-hidden">
            {/* Decorative blur */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/5 rounded-full blur-[60px] pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Case History</p>
                  <h3 className="font-headline text-lg font-bold text-slate-900 tracking-tight">Activity Timeline</h3>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-[11px] top-3 bottom-3 w-px bg-slate-100" />
                <div className="space-y-5">
                  {timeline.map((event, index) => {
                    const Icon = event.icon;
                    return (
                      <div key={index} className="relative flex items-start gap-4 pl-8 group">
                        <div className={`absolute left-0 w-6 h-6 rounded-lg flex items-center justify-center ${event.bgColor} transition-transform group-hover:scale-110`}>
                          <Icon className={`w-3 h-3 ${event.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 leading-tight group-hover:text-primary transition-colors">{event.label}</p>
                          {event.detail && (
                            <p className="text-xs text-slate-500 mt-0.5 break-words italic">"{event.detail}..."</p>
                          )}
                          <p className="text-xs text-slate-500 mt-1 font-semibold">
                            {new Date(event.date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {timeline.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">No activity yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymptomDetail;
